// Dependencies
import React from "react";

// Modules
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";

// App
import { client, serverUrl, apiEndpoints } from "../../config";
import { getOnlineNode } from "../../core/getNode";
import PostBody from "../../partials/postBody";
import { alertMessages } from "../../partials/alertMessages";
import Error from "../../partials/error";
import SkeletonSingleScreen from "../../partials/skeleton-screens/skeletonSingleScreen";
import CommentSection from "../../partials/comments/commentSection";
import { logCustomEvent } from "../../core/analytics";
import Reactions from "../../partials/comments/reactions";
import { favoritesContentType } from "../../utils/helpers";

// UI components
import SinglesHeader from "../../partials/singlesHeader";
import { Row } from "react-bootstrap";

export default function VideosSingle(props) {
  const [isLoading, setIsLoading] = useState(true);
  const [post, setPost] = useState(null);
  const [isError, setIsError] = useState(false);
  const [errorStatus, setErrorStatus] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const user = useSelector((state) => state).authReducer.user;
  const params = useParams();
  const [searchParams] = useSearchParams();
  const hash = searchParams.get("hash");
  const contentType = favoritesContentType("video");

  useEffect(() => {
    loadNode();
    // eslint-disable-next-line
  }, []);

  const loadNode = () => {
    const nid = params.id;

    if (nid) {
      getContent(nid);
      // getComments(nid)
    } else {
      setError(true, 0, alertMessages.contentError);
    }
  };

  /**
   * @function setError
   * @description Updates the state of the component upon an error
   * @param {boolean} _isError
   * @param {int} _errorStatus
   * @param {string} _errorMessage
   */
  const setError = (_isError, _errorStatus, _errorMessage) => {
    setIsLoading(false);
    setIsError(_isError);
    setErrorStatus(_errorStatus);
    setErrorMessage(_errorMessage);
  };

  /**
   * @function getContent
   * @description Retrieves the data from an API / Fallback to local realm object if there is no connection
   */
  const getContent = (nid) => {
    let path;

    if (client === "companyapp") {
      // path = `${apiEndpoints.videos}?nid=${nid}`;
      path = `${apiEndpoints.videos}/all/${nid}?_format=json&status=1&promote=1`;

      if (hash) {
        path = `${path}&auHash=${hash}`;
      }
    } else {
      path = `${apiEndpoints.videos}/all/${nid}?_format=json&status=1&promote=1`;

      if (hash) {
        path = `/api/v2/shared?bundle=video&nid=${nid}&authkey=${hash}`;
      }
    }

    getOnlineNode(path, user.access_token)
      .then((response) => {
        if (response.data.rows.length > 0) {
          setPost(response.data.rows[0]);
          logCustomEvent("read", response.data.rows[0], "Video");
          setIsError(false);
          setIsLoading(false);
        } else {
          setErrorStatus(404);
          setErrorMessage("No data");
          setIsError(true);
        }
      })
      .catch((_error) => {
        if (_error?.response?.status === 403) {
          setErrorStatus(403);
          setErrorMessage("You do not have access to view this content.");
          setIsError(true);
          setIsLoading(false);
        } else {
          setErrorStatus(500);
          setErrorMessage("Failed to get data");
          setIsError(true);
          setIsLoading(false);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const videoLoaded = (id) => {
    const videoElement = document.querySelector(`video[data-nid="${id}"]`);

    new window.Plyr(videoElement, {
      controls: [
        "play-large",
        "play",
        "progress",
        "current-time",
        "mute",
        "volume",
        "fullscreen",
      ],
    });
  };

  const getPlyrEmbedUrl = (url) => {
    try {
      const u = new URL(url);

      if (u.hostname.includes("youtube.com")) {
        return `https://www.youtube.com/embed/${u.searchParams.get("v")}`;
      }
      if (u.hostname.includes("youtu.be")) {
        return `https://www.youtube.com/embed${u.pathname}`;
      }
      if (u.hostname.includes("vimeo.com")) {
        return `https://player.vimeo.com/video${u.pathname}`;
      }

      return url;
    } catch {
      return url;
    }
  };

  const renderPostVideo = (post) => {
    const autoplay = client === "equans" ? true : false;

    let isRemote = false;
    let videoSource;

    if (client === "companyapp") {
      if (post.field_media_oembed_video) {
        videoSource = getPlyrEmbedUrl(post.field_media_oembed_video);
        isRemote = true;
      } else {
        // videoSource = `${serverUrl}/api/v2/assets?mid=${post.mid}`;
        videoSource = `${serverUrl}${
        post.field_media_video_file
          ? post.field_media_video_file
          : post.field_video
      }`;
      }

      const fileToken = post.file_access_token;

      if (fileToken && !isRemote) {
        videoSource = `${serverUrl}/shared/files/${post.filename}?access_token=${fileToken}`;
      }
    } else {
      videoSource = `${serverUrl}${
        post.field_media_video_file
          ? post.field_media_video_file
          : post.field_video
      }`;
    }

    return (
      <div className="post-video-container" style={{ position: "relative" }}>
        {isRemote ? (
          <div className="plyr__video-embed">
            <iframe
              src={videoSource}
              allowFullscreen
              allow="autoplay; encrypted-media"
              title={post.title}
            ></iframe>
          </div>
        ) : (
          <video
            data-nid={post.nid}
            controls
            autoPlay={autoplay}
            className="post-video plyr"
            onLoadedMetadata={() => {
              videoLoaded(post.nid);
            }}
          >
            <source src={videoSource} alt={`Video for ${post.title}`} />
          </video>
        )}
      </div>
    );
  };

  if (isLoading) {
    return <SkeletonSingleScreen />;
  } else {
    if (isError) {
      return <Error status={errorStatus} message={errorMessage} />;
    } else {
      let category = "Uncategorised";

      if (post.category_labels) {
        category = post.category_labels;
      }

      return (
        <main className="videos single" data-nid={post.nid}>
          <article className="article">
            <SinglesHeader
              route={"/videos"}
              backBtnText={"Video Library"}
              category={category}
              timestamp={post.created}
              title={post.title}
              hash={hash}
            />
            {renderPostVideo(post)}
            <PostBody body={post.body} />
            <div className="article__body"></div>
            {user && user.current_user && (
              <>
                <Row className="reaction-container gx-0">
                  <Reactions
                    user={user}
                    nid={post.nid}
                    contentType={contentType}
                    field_allow_reactions={post.field_allow_reactions}
                    field_access_token={post.field_access_token}
                    title={post.title}
                    shareEnabled
                  />
                </Row>
                <CommentSection
                  nid={post.nid}
                  field_allow_reactions={post.field_allow_reactions}
                  field_allow_comments={post.field_allow_comments}
                />
              </>
            )}
          </article>
        </main>
      );
    }
  }
}
